import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Landing from "./pages/Landing";
import Airports from "./pages/Airports";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";
import AirportDetail from "./pages/AirportDetail";
import AirportGuide from "./pages/AirportGuide";
import AirportTerminals from "./pages/AirportTerminals";
import TerminalRoute from "./pages/TerminalRoute";
import AirportPreCheck from "./pages/AirportPreCheck";
import AirportParking from "./pages/AirportParking";
import AirportDining from "./pages/AirportDining";
import AirportDiningTerminal from "./pages/AirportDiningTerminal";
import DiningHub from "./pages/DiningHub";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import GuidesIndex from "./pages/GuidesIndex";
import GuidePost from "./pages/GuidePost";
import Install from "./pages/Install";
import AppRedirect from "./pages/AppRedirect";
import MinimizeWaitTimes from "./pages/MinimizeWaitTimes";
import RealTimeWaitTimes from "./pages/RealTimeWaitTimes";
import Survey from "./pages/Survey";
import AdminSurveyEmail from "./pages/AdminSurveyEmail";
import AdminMetrics from "./pages/AdminMetrics";
import { RouteTracker } from "./components/RouteTracker";
import Pricing from "./pages/Pricing";
import SavedRestaurants from "./pages/SavedRestaurants";
import OAuthConsent from "./pages/OAuthConsent";
import { useAuth } from "./hooks/useAuth";
import { usePlatform } from "./hooks/usePlatform";
import { ProProvider } from "./hooks/useProStatus";
import { ChatBot } from "./components/ChatBot";
import { FeedbackWidget } from "./components/FeedbackWidget";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { lazy, Suspense } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { isNativeCapacitorApp } from "./lib/runtime";

const isNativeShell = isNativeCapacitorApp();

const IOSApp = lazy(() => import("./pages/ios/IOSApp"));

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

const WIDGET_HIDDEN_ROUTES = ['/survey', '/auth', '/.lovable/oauth/consent'];

const AppContent = () => {
  const location = useLocation();
  const { isNativeIOS, isNative } = usePlatform();
  const isIOSRoute = location.pathname.startsWith("/ios");
  const showWidgets = !WIDGET_HIDDEN_ROUTES.includes(location.pathname) && !isIOSRoute;
  const showPWAChrome = !isNative && !isIOSRoute;
  const nativeStartRoute = isNativeIOS || isNative;

  return (
    <>
      <RouteTracker />
      <Routes>
        {/* iOS native app routes */}
        <Route
          path="/ios/*"
          element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="text-center text-muted-foreground">Loading...</div></div>}>
              <IOSApp />
            </Suspense>
          }
        />
        <Route path="/index" element={<Navigate to={nativeStartRoute ? "/ios" : "/"} replace />} />
        {/* Redirect native app users to the native experience */}
        <Route path="/" element={nativeStartRoute ? <Navigate to="/ios" replace /> : <Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/guides" element={<GuidesIndex />} />
        <Route path="/guides/:slug" element={<GuidePost />} />
        <Route path="/install" element={<Install />} />
        <Route path="/app" element={<AppRedirect />} />
        <Route path="/tips/minimize-wait-times" element={<MinimizeWaitTimes />} />
        <Route path="/real-time-wait-times" element={<RealTimeWaitTimes />} />
        <Route path="/survey" element={<Survey />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/admin/survey-email" element={<ProtectedRoute><AdminSurveyEmail /></ProtectedRoute>} />
        <Route path="/admin/metrics" element={<ProtectedRoute><AdminMetrics /></ProtectedRoute>} />
        <Route path="/airports" element={<Airports />} />
        <Route path="/airports/:code" element={<AirportDetail />} />
        <Route path="/airports/:code/guide" element={<AirportGuide />} />
        <Route path="/airports/:code/terminals" element={<AirportTerminals />} />
        <Route path="/airports/:code/precheck" element={<AirportPreCheck />} />
        <Route path="/airports/:code/terminals/:airline" element={<TerminalRoute />} />
        <Route path="/airports/:code/parking" element={<AirportParking />} />
        <Route path="/airports/:code/dining" element={<AirportDining />} />
        <Route path="/airports/:code/dining/:terminal" element={<AirportDiningTerminal />} />
        <Route path="/airport-dining" element={<DiningHub />} />
        <Route
          path="/home"
          element={
            nativeStartRoute ? (
              <Navigate to="/ios" replace />
            ) : (
              <ProtectedRoute>
                <Index />
              </ProtectedRoute>
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            nativeStartRoute ? (
              <Navigate to="/ios" replace />
            ) : (
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            )
          }
        />
        <Route
          path="/saved-restaurants"
          element={
            <ProtectedRoute>
              <SavedRestaurants />
            </ProtectedRoute>
          }
        />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {showWidgets && <ChatBot />}
      {showWidgets && <FeedbackWidget />}
      {showPWAChrome && <PWAInstallPrompt />}
      {showPWAChrome && <OfflineIndicator />}
      {showPWAChrome && <UpdatePrompt />}
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          <ProProvider>
            <Toaster />
            {!isNativeShell && <Sonner />}
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </ProProvider>
        </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
